<template>
  <div id="users-index">
    <div class="flex justify-between py-2">
      <search-title class="flex-1" @search="updateSearchQuery">{{
        i18n.t("navigation.users")
      }}</search-title>
      <div class="flex items-center mt-5">
        <div
          v-if="filteredUsers && filteredUsers.length > 0"
          class="hidden md:block text-gray-600"
        >
          {{ filteredUsers.length }}
          {{ i18n.t("general.results") }}
        </div>
        <h2 v-else>
          {{ i18n.t("users.empty") }}
        </h2>
      </div>
    </div>

    <div
      class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-screen-2xl mx-auto"
    >
      <!-- BEGIN: Users Layout -->
      <div
        v-for="user in filteredUsers"
        :key="user.id"
        :class="[
          'box p-4 cursor-pointer',
          user.role === 'printing' ? 'ring-2 ring-blue-500 bg-blue-50' : '',
        ]"
      >
        <!-- name -->
        <div class="text-2xl font-bold">{{ user.name }}</div>

        <!-- address -->
        <div class="text-lg my-1">{{ i18n.t(`user.role.${user.role}`) }}</div>

        <!-- contacts -->
        <div class="text-xs mb-1">{{ user.email }}</div>

        <!-- printing user password button -->
        <div v-if="user.role === 'printing'" class="mt-3">
          <button
            @click="openPrintingPasswordModal(user)"
            class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
          >
            {{ i18n.t("users.changePrintingPassword") }}
          </button>
        </div>
      </div>
      <!-- BEGIN: Users Layout -->
    </div>

    <!-- Printing Password Change Modal -->
    <div
      v-if="showPrintingPasswordModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click="closePrintingPasswordModal"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4" @click.stop>
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold">
            {{ i18n.t("users.changePrintingPasswordFor") }}
            {{ selectedPrintingUser?.name }}
          </h3>
          <button
            @click="closePrintingPasswordModal"
            class="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <!-- Error Message -->
        <div
          v-if="errorMessage"
          class="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md"
        >
          {{ errorMessage }}
        </div>

        <!-- Success Message -->
        <div
          v-if="successMessage"
          class="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md"
        >
          {{ successMessage }}
        </div>

        <form @submit.prevent="updatePrintingPassword">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ i18n.t("users.newPrintingPassword") }}
            </label>
            <input
              v-model="newPrintingPassword"
              type="password"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              minlength="8"
            />
          </div>

          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ i18n.t("users.confirmPrintingPassword") }}
            </label>
            <input
              v-model="confirmPrintingPassword"
              type="password"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              minlength="8"
            />
          </div>

          <div class="flex justify-end space-x-3">
            <button
              type="button"
              @click="closePrintingPasswordModal"
              class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="loading || closingAfterSuccess"
            >
              {{ i18n.t("general.cancel") }}
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-500"
              :disabled="loading || closingAfterSuccess"
            >
              {{
                loading ? i18n.t("general.updating") : i18n.t("general.update")
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useI18n } from "vue3-i18n";
import { useStore } from "vuex";
import { useCollectionSearch } from "@/composables/collection-search";

export default {
  setup() {
    const i18n = useI18n();
    const store = useStore();
    const {
      updateSearchQuery,
      collectionToFilter,
      filteredCollection: filteredUsers,
    } = useCollectionSearch(["name", "email"]);

    // Printing Password Modal state
    const showPrintingPasswordModal = ref(false);
    const selectedPrintingUser = ref(null);
    const newPrintingPassword = ref("");
    const confirmPrintingPassword = ref("");
    const loading = ref(false);
    const closingAfterSuccess = ref(false);
    const errorMessage = ref("");
    const successMessage = ref("");

    // fetch users
    async function fetchUsers() {
      const result = await store.dispatch("users/getCollection");
      collectionToFilter.value = result.validated;
    }
    fetchUsers();

    // Printing Password Modal functions
    function openPrintingPasswordModal(user) {
      selectedPrintingUser.value = user;
      newPrintingPassword.value = "";
      confirmPrintingPassword.value = "";
      showPrintingPasswordModal.value = true;
    }

    function closePrintingPasswordModal() {
      showPrintingPasswordModal.value = false;
      selectedPrintingUser.value = null;
      newPrintingPassword.value = "";
      confirmPrintingPassword.value = "";
      closingAfterSuccess.value = false;
      errorMessage.value = "";
      successMessage.value = "";
    }

    async function updatePrintingPassword() {
      // Clear previous messages
      errorMessage.value = "";
      successMessage.value = "";

      if (newPrintingPassword.value !== confirmPrintingPassword.value) {
        errorMessage.value = i18n.t("users.printingPasswordMismatch");
        return;
      }

      if (!newPrintingPassword.value || newPrintingPassword.value.length < 8) {
        errorMessage.value = i18n.t("users.printingPasswordTooShort");
        return;
      }

      loading.value = true;
      try {
        const result = await store.dispatch("users/updatePrintingPassword", {
          userId: selectedPrintingUser.value.id,
          password: newPrintingPassword.value,
        });

        if (result.success) {
          successMessage.value = i18n.t("users.printingPasswordUpdated");
          closingAfterSuccess.value = true;
          // Close modal after a short delay to show success message
          setTimeout(() => {
            closePrintingPasswordModal();
          }, 1500);
        } else {
          errorMessage.value = i18n.t("users.printingPasswordUpdateError");
        }
      } catch (error) {
        errorMessage.value = i18n.t("users.printingPasswordUpdateError");
      } finally {
        loading.value = false;
      }
    }

    return {
      i18n,
      updateSearchQuery,
      filteredUsers,
      showPrintingPasswordModal,
      selectedPrintingUser,
      newPrintingPassword,
      confirmPrintingPassword,
      loading,
      closingAfterSuccess,
      errorMessage,
      successMessage,
      openPrintingPasswordModal,
      closePrintingPasswordModal,
      updatePrintingPassword,
    };
  },
};
</script>

<style></style>
